import { FilterModel } from '@hooks/useFiltersSearchParams';
import { PaginationModel } from '@hooks/usePagingSearchParams';
import { sellerApiClient, getParams } from './sellerApiClient';
import { TAmountWithCurrency } from '@api/apiClient/returns';
import { TWeChatWallet } from '@src/pages/ProfilePage/types';
import { roundMoney } from '@utils/formatValues';

export const getFinancePage = async ({
  page,
  filters,
}: {
  page: PaginationModel;
  filters?: FilterModel[];
}): Promise<Response<GetWalletOperationsItem>> => {
    const response = await sellerApiClient.get<Response<GetWalletOperationsItem>>(
      "/v1/wallet/operations/page",
      {
        params: getParams(page, filters),
        paramsSerializer: { indexes: null },
      },
    );

    return response.data;
};

interface Response<T> {
  total: number,
  items: T[],
}

export interface GetWalletOperationsItem {
  id: string;
  date: string;
  amount: number;
  type: string; // RecyclingCompensation;
  status: string; // Waiting, Approved, Done, Cancelled
  details: GetWalletOperationsDetails;
}

interface GetWalletOperationsDetails {
  parcelId: string;
  trackNumber: string;
  parcelComplaintId: string;
  sellerId: string;
  returnPostingNumber: string;
  requestNumber: string;
  date: string;
}

type TGetWalletsResponse = {
  balance: number;
  hasActiveWithdrawal: boolean;
  weChatWithdrawalLimit: number;
};

export const getWallet = async (): Promise<TGetWalletsResponse> => {
  const response = await sellerApiClient.get('/v1/wallet');
  return response.data;
};

export const removeWallet = async (id: string): Promise<void> => {
  const response = await sellerApiClient.delete<void>(
    `/v1/wallet/wechat/wallets/${id}`,
  );

  return response.data;
};

export interface WalletAuthRequest {
  requestId: string;
  qrCode: string;
}

export const authorizeWallet = async (): Promise<WalletAuthRequest> => {
  const response = await sellerApiClient.post<WalletAuthRequest>(
    "/v1/wallet/wechat",
  );

  return response.data;
};

export const getWalletRequestStatus = async (requestId: string): Promise<WalletRequestStatus> => {
  const response = await sellerApiClient.get<WalletRequestStatus>(
    `/v1/wallet/wechat/link/${requestId}`
  );

  return response.data;
};

export interface WalletRequestStatus {
  requestId: string;
  isProcessed: boolean;
  isSuccess: boolean;
  errorCode: string;
}

type TGetWeChatWalletsResponse = {
  items: TWeChatWallet[];
};

export const getWeChatWallets = async (): Promise<TGetWeChatWalletsResponse> => {
  const response = await sellerApiClient.get('/v1/wallet/wechat/wallets');
  return response.data;
};

export const makeWeChatWalletActive = async (id: string): Promise<void> => {
  await sellerApiClient.post(`/v1/wallet/wechat/${id}`);
};

export type sendWithdrawFundsRequest = {
  amount: number;
  weChatWalletId: string;
};

export const sendWithdrawFunds = async (data: sendWithdrawFundsRequest): Promise<void> => {
  await sellerApiClient.post('/v1/wallet/wechat/withdraw', data);
};

export type sendTopUpBalanceRequest = {
  amount: number;
};

export type sendTopUpBalanceResponse = {
  outTradeNo: string;
  paymentUrl?: string;
  qrCode: string;
};

export const sendTopUpBalance = async (data: sendTopUpBalanceRequest): Promise<sendTopUpBalanceResponse> => {
  const response = await sellerApiClient.post('/v1/wechat/topup-by-wechat', {
    ...data,
    amount: roundMoney(data.amount),
  });
  return response.data;
};

export type checkBalanceReplenishmentResponse = {
  amount: number;
  errorCode: string;
  isProcess: boolean;
  isSuccess: boolean;
};

export const checkBalanceReplenishment = async (outTradeNo: string): Promise<checkBalanceReplenishmentResponse> => {
  const response = await sellerApiClient.get(`/v1/wechat/payment/status/${outTradeNo}`);
  return response.data;
};

export type TGetPaymentLink = {
  paymentLink: string;
  paymentRef: string;
};

export type TGetPaymentLinkResponse = TGetPaymentLink;

export const getPaymentLink = async (amount: string): Promise<TGetPaymentLinkResponse> => {
  const response = await sellerApiClient.post(`/v1/ASE/payment-link?amount=${amount}`);
  return response.data;
};

export type TCheckPaymentLinkStatusResponse = {
  amount?: number;
  isPaid: boolean;
};

export const checkPaymentLinkStatus = async (paymentRef: string): Promise<TCheckPaymentLinkStatusResponse> => {
  const response = await sellerApiClient.get(`/v1/ASE/payment-status/${paymentRef}`);
  return response.data;
};

export type TAutoAction = {
  enabled: boolean;
  id: string;
  missingAmount?: TAmountWithCurrency
  type: string;
};

export type TGetAutoActions = {
  autoActions: TAutoAction[];
};

export type TGetAutoActionsResponse = TGetAutoActions;

export const getAutoActions = async (): Promise<TGetAutoActionsResponse> => {
  const response = await sellerApiClient.get('/v1/auto-actions');
  return response.data;
};
